import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Home, Euro, Users } from "lucide-react";
import { todayIso } from "@/lib/api";

const empty = {
  property_address: "",
  client_name: "",
  sale_price: 0,
  agency_commission: 0,
  negotiator_commission: 0,
  status: "compromis",
  signed_date: todayIso(),
  notes: "",
  is_co_sale: false,
  co_sale_ref: "",
};

export default function SaleFormDialog({ open, onClose, onSubmit, initial, saving }) {
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k, v) => set(k, Math.max(0, Number(v) || 0));

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      sale_price: Number(form.sale_price) || 0,
      agency_commission: Number(form.agency_commission) || 0,
      negotiator_commission: Number(form.negotiator_commission) || 0,
      is_co_sale: !!form.is_co_sale,
      co_sale_ref: form.is_co_sale ? (form.co_sale_ref || "").trim() : "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial ? "Modifier la vente" : "Ajouter une vente"}
          </DialogTitle>
          <DialogDescription>
            Enregistrez un compromis signé ou un acte définitif avec la commission perçue.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="sale-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Bien / Adresse</Label>
              <div className="relative">
                <Home className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="sale-address"
                  value={form.property_address}
                  onChange={(e) => set("property_address", e.target.value)}
                  className="h-11 pl-9"
                  placeholder="Ex : Appartement T3 – 14 rue de Rivoli, Paris"
                />
              </div>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Client (facultatif)</Label>
              <Input
                data-testid="sale-client"
                value={form.client_name}
                onChange={(e) => set("client_name", e.target.value)}
                className="h-11"
                placeholder="M. Martin"
              />
            </div>

            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Prix de vente</Label>
              <div className="relative">
                <Euro className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="sale-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.sale_price}
                  onChange={(e) => setNum("sale_price", e.target.value)}
                  className="h-11 pl-9 font-semibold"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Commission agence</Label>
              <div className="relative">
                <Euro className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="sale-agency-commission"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.agency_commission}
                  onChange={(e) => setNum("agency_commission", e.target.value)}
                  className="h-11 pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Part négociateur</Label>
              <div className="relative">
                <Euro className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="sale-negotiator-commission"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.negotiator_commission}
                  onChange={(e) => setNum("negotiator_commission", e.target.value)}
                  className="h-11 pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Statut</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="sale-status" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compromis">Compromis signé</SelectItem>
                  <SelectItem value="acte">Acte définitif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Date de signature</Label>
              <Input
                data-testid="sale-date"
                type="date"
                value={form.signed_date}
                onChange={(e) => set("signed_date", e.target.value)}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Notes</Label>
              <Textarea
                data-testid="sale-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                className="min-h-[70px]"
                placeholder="Contexte, remarques…"
              />
            </div>

            <div className="col-span-2 rounded-md border border-border p-3 space-y-3 bg-slate-50">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.is_co_sale}
                  onCheckedChange={(v) => set("is_co_sale", !!v)}
                  data-testid="sale-is-co-sale"
                />
                <span className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-primary" />
                  Co-vente — plusieurs négociateurs sur ce bien
                </span>
              </label>
              {form.is_co_sale ? (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Référence de la co-vente</Label>
                  <Input
                    data-testid="sale-co-sale-ref"
                    value={form.co_sale_ref}
                    onChange={(e) => set("co_sale_ref", e.target.value)}
                    className="h-10 bg-white"
                    placeholder="Ex : 12 rue Foch (référence commune à saisir par chaque négo)"
                  />
                  <p className="text-xs text-slate-500">
                    Les négociateurs qui saisissent la même référence partagent la vente : le prix n'est compté qu'une fois dans le CA de l'agence.
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="sale-form-submit" className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {initial ? "Enregistrer" : "Ajouter la vente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
